import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Skeleton } from '@/nova/components';
import { Sheet } from '@/nova/components/Sheet';
import { formatCompact } from '@/nova/utils/money';
import { useAppSelector } from '@/store';
import { selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';

import type { AdLanguageId } from '../../ad-language';
import { fetchSiteLanguages, siteDefaultLanguage } from '../../ad-language-api';
import { adBrandFrom, adSubjectFromItem, subjectLandingUrl, type GameLogos } from '../../ad-subjects';
import { newestLockedVersion, withCreated } from '../../ad-versions';
import { adTabRoutes, type AdView } from '../../asset-routes';
import type { AdBrand, AdSubject } from '../../banner-kit-api';
import { applyBrandTheme, fetchBrandTheme } from '../../brand-theme-api';
import type { CreativeItem } from '../../creative-api';
import {
  DEFAULT_PLAYABLE_TIMING,
  fetchPlayableTemplates,
  isMiniGame,
  upsertPlayable,
  type Playable,
  type PlayableParams,
  type PlayableStats,
  type PlayableTemplatesResponse,
} from '../../playable-api';
import { posterVersion } from '../../playable-poster';
import { useAssetLibraryStats } from '../../useAssetLibraryStats';
import { STATS_SHEET_PARAM, useSheetParam } from '../../useSheetParam';
import { usePlayableLibrary } from '../../usePlayableLibrary';
import { detectWelcomeBonus, withGameLogos, withLockedMiniGame } from '../../welcome-bonus';
import { AdLibraryShell, type AdCardState } from '../AdLibraryShell';
import { AssetCardStats, AssetCardStatsSkeleton, AssetStatsSheet } from '../AssetStats';
import { LumioMark } from '../LumioMark';
import { PlayableComposer } from '../PlayableComposer';
import { GamePoster } from './GamePoster';
import * as S from './PlayableLibrary.styles';

export interface PlayableLibraryProps {
  tenantId?: number;
  brandName: string;
  brandDomain?: string;
  /** The brand's mini-game logos (the primary logo and the bare symbol): a new game is made with them, and every game
   *  is previewed with them (see `withGameLogos`). */
  brandLogos?: GameLogos;
  /** The brand's promotion pool (a mini-game advertises the welcome bonus picked out of it; the tracking link's
   *  `cta_href`). */
  items: CreativeItem[];
  itemsLoaded: boolean;
  /** The deep-linked view (library grid, create new, or a mini-game by id), derived from the URL by the page. */
  view: AdView;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
}

/** The formats a new mini-game starts with (every template offers them). */
const NEW_FORMATS = ['300x600', '320x480', 'responsive'];

/** A new mini-game's document: the promotion it advertises (the welcome bonus), no game chosen yet (the composer's
 *  first step), its locked words (see `withLockedMiniGame`: the promotion's own offer and call to action, the game
 *  text in the ad's language), the brand (with its website theme), the UK/EU legal line, the three formats every
 *  template offers, the promotion's landing page, the runtime's default timing (muted until a tap) and the ad's
 *  language (the website's default; '' while unknown, filled in by the composer later). Pure. */
const newPlayableParams = (
  subject: AdSubject,
  brand: AdBrand,
  landingUrl: string,
  language: AdLanguageId,
): PlayableParams =>
  withLockedMiniGame({
    subject_type: 'promotion',
    subject,
    template_id: '',
    schema_version: 1,
    config: {},
    copy: { headline: '', instruction: '', reveal_label: '', cta: '' },
    offer: { title: '', terms: '', tc_url: '' },
    brand,
    legal: { enabled: true, preset: 'uk_eu', text: '' },
    formats: [...NEW_FORMATS],
    art_direction: '',
    landing_url: landingUrl,
    timing: { ...DEFAULT_PLAYABLE_TIMING },
    audio: { default_muted: true },
    language,
  });

/** The card's background plate: the plate of the version the card shows (`posterVersion`), '' when nothing has been
 *  painted (the archived list's thumbnail, and the poster until its game has loaded). Never the subject key art the
 *  draft was minted with, which would pass a not-yet-generated mini-game off as a finished one. Pure. */
const posterUrl = (p: Playable): string => posterVersion(p)?.assets.background_url ?? '';

const isActive = (job: CreativeJob | undefined): boolean => job?.status === 'queued' || job?.status === 'running';

/** Whether a hosted embed of the mini-game has reported anything (any counter above zero). Pure. */
export const hasEmbedStats = (stats: PlayableStats): boolean =>
  stats.impressions > 0 || stats.interactions > 0 || stats.reveals > 0 || stats.clicks > 0;

/** A card's hosted-embed stats (30 days): views (impressions), plays (a first interaction), reveals and clicks
 *  (CTA presses), once an embed has reported any (the tracking-link figures under them are the card's stats until
 *  then). */
const EmbedStats = ({ stats }: { stats: PlayableStats }): React.ReactElement | null => {
  if (!hasEmbedStats(stats)) return null;
  return (
    <S.StatsRow>
      <S.Stat>
        <S.StatValue raw text={formatCompact(stats.impressions)} />
        <S.StatLabel text="Views" />
      </S.Stat>
      <S.Stat>
        <S.StatValue raw text={formatCompact(stats.interactions)} />
        <S.StatLabel text="Plays" />
      </S.Stat>
      <S.Stat>
        <S.StatValue raw text={formatCompact(stats.reveals)} />
        <S.StatLabel text="Reveals" />
      </S.Stat>
      <S.Stat>
        <S.StatValue raw text={formatCompact(stats.clicks)} />
        <S.StatLabel text="Clicks" />
      </S.Stat>
    </S.StatsRow>
  );
};

/** A mini-game's card state: an art run in flight, generated art (a locked version), or a draft. Pure. */
const cardState = (p: Playable, jobsById: Record<string, CreativeJob>): AdCardState => {
  if (isActive(p.job_id ? jobsById[p.job_id] : undefined)) return 'generating';
  return newestLockedVersion(p.ad_versions) ? 'ready' : 'draft';
};

/**
 * Marketing Assets > Mini-games: the brand's branded HTML5 mini-game ads on the shared ad library shell (create
 * tile, cards with the game at rest over its generated background as poster, Edit / Archive, archived sheet). Only
 * playables of a `game` template are listed (the `banner` ones belong to HTML5 Banners). A mini-game always
 * advertises the brand's welcome bonus: creating one NAVIGATES to `/mini-games/new`, which mints the draft with the
 * detected welcome bonus (and the website theme) and swaps the URL to its id in place, so the composer opens
 * straight on the game picker; opening one navigates to `/mini-games/:id`. The URL stays the single source of
 * truth, so browser back/forward and reloads work.
 */
export const PlayableLibrary = ({
  tenantId,
  brandName,
  brandDomain,
  brandLogos,
  items,
  itemsLoaded,
  view,
  onBuyCredits,
  onNotify,
}: PlayableLibraryProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routes = adTabRoutes('playables');
  const {
    items: games,
    loading,
    loaded,
    settled,
    listFailed,
    refresh,
    remove,
    restore,
    purge,
    patch,
  } = usePlayableLibrary(tenantId);
  const jobsById = useAppSelector(selectCreativeJobsMap);
  const viewId = view.kind === 'edit' ? view.id : undefined;

  // The template registry (labels for the card meta, and the runtime each card draws its game at rest with): static
  // per deploy and cached by the client (one read shared with the composer).
  const [registry, setRegistry] = React.useState<PlayableTemplatesResponse | null>(null);
  const [registryFailed, setRegistryFailed] = React.useState(false);
  React.useEffect(() => {
    let live = true;
    void fetchPlayableTemplates().then((r) => {
      if (!live) return;
      if (r) setRegistry(r);
      else setRegistryFailed(true);
    });
    return () => {
      live = false;
    };
  }, []);

  // `new`: the draft is minted with the welcome bonus and the URL swapped to its id WITHOUT navigating
  // (navigate() would push a second history entry for the same mini-game and re-run the URL sync; replaceState keeps
  // this instance and the router's history state, so back/forward still work and a reload reopens this draft
  // instead of minting another). `createdRef` stops a double mint; `createGenRef` lets a stale mint (the owner left
  // and started over) tell it is no longer the current attempt.
  const [created, setCreated] = React.useState<Playable | null>(null);
  const createdRef = React.useRef(false);
  const createGenRef = React.useRef(0);
  // The promotion a new mini-game advertises: the brand's welcome bonus (undefined when it has no promotion).
  const welcome = React.useMemo(() => detectWelcomeBonus(items), [items]);
  const noPromotion = itemsLoaded && !welcome;

  // The library does not remount across /mini-games <-> /mini-games/new <-> /mini-games/:id (the page keys it by
  // tab), so returning to the grid resets the new-game session; the grid is the one view with no mint in flight.
  React.useEffect(() => {
    if (view.kind !== 'library') return;
    createdRef.current = false;
    createGenRef.current += 1; // invalidate any in-flight mint
    setCreated(null);
  }, [view.kind]);

  // The brand's website theme (palette, CTA button, fonts) and the website's languages (the default is the new
  // mini-game's language) prefill every new mini-game; fetched early so a create does not wait on them (cached per
  // tenant, never throw).
  React.useEffect(() => {
    if (!tenantId) return;
    void fetchBrandTheme(tenantId);
    void fetchSiteLanguages(tenantId);
  }, [tenantId]);

  // `edit`: resolved from the list (there is no single read). Only a SETTLED list for this tenant may declare the id
  // missing; then leave to the grid with a notice.
  React.useEffect(() => {
    // Without the registry no playable can be told to be a mini-game: back to the grid, which says it couldn't load.
    if (view.kind === 'edit' && registryFailed) {
      navigate(routes.library(), { replace: true });
      return;
    }
    // A playable of a `banner` template is an HTML5 banner, not a mini-game: only the registry tells them apart.
    if (view.kind !== 'edit' || !viewId || !loaded || loading || !registry) return;
    // The asset minted here moments ago is not missing while the list catches up: opening its stats or links sheet moves
    // the address from /new to its id before the post-mint refresh has landed it.
    if (created && created.tenant_id === String(tenantId) && created.id === viewId) return;
    if (!games.some((g) => g.id === viewId && isMiniGame(g, registry.templates))) {
      onNotify(t("That mini-game couldn't be found."));
      navigate(routes.library(), { replace: true });
    }
  }, [
    view.kind,
    viewId,
    loaded,
    loading,
    registry,
    registryFailed,
    games,
    created,
    tenantId,
    routes,
    onNotify,
    navigate,
    t,
  ]);

  const goToLibrary = React.useCallback((): void => navigate(routes.library()), [navigate, routes]);
  // The composer's optimistic edits (name, document, versions, run link) land on the list entry, and on the freshly
  // minted snapshot too while the post-mint refresh has not brought the mini-game into the list yet.
  const patchGame = React.useCallback(
    (id: string, fields: Partial<Playable>): void => {
      patch(id, fields);
      setCreated((cur) => (cur && cur.id === id ? { ...cur, ...fields } : cur));
    },
    [patch],
  );
  const startNew = React.useCallback((): void => navigate(routes.create()), [navigate, routes]);
  const openGame = React.useCallback((p: Playable): void => navigate(routes.edit(p.id)), [navigate, routes]);

  const mint = React.useCallback(
    (subject: AdSubject): void => {
      if (!tenantId || createdRef.current) return;
      createdRef.current = true;
      const gen = (createGenRef.current += 1);
      const name = subject.name || t('Untitled mini-game');
      const fail = (): void => {
        // A stale attempt must not release a newer attempt's re-entry lock (that would allow a duplicate draft).
        if (gen !== createGenRef.current) return;
        createdRef.current = false;
        onNotify(t("That couldn't be created. Please try again."));
        navigate(routes.library(), { replace: true });
      };
      // The website theme (cached, bounded wait, null when the site has none) and the website's default language (the
      // ad's language; '' when it could not be read, filled in by the composer later) first, then the draft.
      void Promise.all([fetchBrandTheme(tenantId), siteDefaultLanguage(tenantId)]).then(([theme, language]) => {
        if (gen !== createGenRef.current) return;
        const adParams = withGameLogos(
          newPlayableParams(
            subject,
            applyBrandTheme(adBrandFrom(brandName, brandDomain, brandLogos?.logo_url), theme),
            subjectLandingUrl(subject, brandDomain),
            language,
          ),
          brandLogos,
        );
        void upsertPlayable(tenantId, {
          name,
          status: 'draft',
          image_url: subject.image_url,
          ad_params: adParams,
        }).then(
          (id) => {
            if (gen !== createGenRef.current) return;
            if (!id) {
              fail();
              return;
            }
            setCreated({
              id,
              tenant_id: String(tenantId),
              name,
              media_type: 'playable',
              image_url: subject.image_url,
              status: 'draft',
              archived: false,
              ad_params: adParams,
              ad_versions: [],
              stats: { impressions: 0, interactions: 0, reveals: 0, clicks: 0 },
            });
            window.history.replaceState(window.history.state, '', routes.edit(id));
            refresh();
          },
          fail, // `fail` covers the request only: once the row exists, nothing may unlock a second mint
        );
      });
    },
    [tenantId, brandName, brandDomain, brandLogos, routes, refresh, onNotify, navigate, t],
  );

  // `new` goes straight to the game: once the brand's promotions are known, the draft is minted with its welcome
  // bonus (the owner can change the promotion in the composer). A brand with no promotion has nothing to advertise:
  // back to the grid, whose create tile says so.
  React.useEffect(() => {
    if (view.kind !== 'new' || !tenantId || !itemsLoaded || createdRef.current) return;
    if (!welcome) {
      onNotify(t('This brand has no promotions yet. Create one in All Promotions & Creatives first.'));
      navigate(routes.library(), { replace: true });
      return;
    }
    mint(adSubjectFromItem(welcome));
  }, [view.kind, tenantId, itemsLoaded, welcome, mint, onNotify, navigate, routes, t]);

  // The mini-game the editor opens: the freshly minted draft for `new` (the live list entry once the post-mint
  // refresh lands it), else the one matching the :id. A cold deep link waits for the list.
  const editing = view.kind !== 'library';
  // The freshly minted draft counts only for the brand it was made for: right after a brand switch it must not stand
  // in for the (still empty) new brand's list, or its editor would render, and save, under the new brand.
  const createdHere = created && created.tenant_id === String(tenantId) ? created : null;
  const reopen: Playable | undefined =
    view.kind === 'new'
      ? createdHere
        ? withCreated(games, createdHere)
        : undefined
      : viewId && createdHere?.id === viewId
        ? withCreated(games, createdHere)
        : viewId && registry
          ? games.find((g) => g.id === viewId && isMiniGame(g, registry.templates))
          : undefined;

  // The editor, keyed by the mini-game so opening another one starts from its own document. Closing it returns to
  // the grid (the URL is the source of truth).
  const composerNode =
    editing && reopen ? (
      <PlayableComposer
        key={`${tenantId}:${reopen.id}`}
        tenantId={tenantId}
        playable={reopen}
        brandName={brandName}
        brandDomain={brandDomain}
        gameLogos={brandLogos}
        items={items}
        patch={patchGame}
        onClose={goToLibrary}
        onBuyCredits={onBuyCredits}
        onNotify={onNotify}
      />
    ) : null;

  // The mini-games only: a playable of a `banner` template is an HTML5 banner (listed in its own tab). Listed once the
  // registry is known: before it, every playable reads as a mini-game.
  const miniGames = registry ? games.filter((g) => isMiniGame(g, registry.templates)) : [];
  // The final list needs the brand's playables AND the registry that tells a mini-game from a banner.
  const listReady = settled && (registry !== null || registryFailed);
  const archivedCount = miniGames.filter((g) => g.archived).length;
  // Each card's tracking-link figures (one grouped read for the grid; an entry iff the game has a link), re-read when
  // a composer closes; a card's figures open the game's stats sheet.
  const liveIds = miniGames.filter((g) => !g.archived).map((g) => g.id);
  const stats = useAssetLibraryStats('playable', liveIds, {
    onGrid: view.kind === 'library',
    idsLoaded: loaded && !listFailed,
  });
  // The stats sheet open over the grid is the address's (`?stats=<id>`), so Back from one of its deep links reopens it.
  // Read on the grid only: a composer open over it reads the same key for its own sheet.
  const statsSheet = useSheetParam(STATS_SHEET_PARAM);
  const statsForId = view.kind === 'library' ? statsSheet.value : null;
  const statsFor = statsForId ? miniGames.find((g) => g.id === statsForId && !g.archived) : undefined;
  const templateLabel = (id: string): string | undefined => registry?.templates.find((tpl) => tpl.id === id)?.label;

  return (
    <>
      <AdLibraryShell<Playable>
        kind="playable"
        items={miniGames}
        ready={listReady}
        title={t('Your mini-games')}
        intro={t(
          'Every mini-game you have made. Open one to edit it, generate its art or export it, archive it, or create a new one.',
        )}
        archivedTitle={t('Archived mini-games ({{n}})', { n: archivedCount })}
        archivedEmptyText={t('No archived mini-games.')}
        emptyText={
          // A failed registry read hides every mini-game: never shown as "none yet" (a reload reads it again).
          registryFailed || listFailed
            ? t("Your mini-games couldn't be loaded. Reload the page to try again.")
            : t('No mini-games yet. Create your first one.')
        }
        loadingText={t('Loading your mini-games…')}
        createLabel={t('Create new')}
        createAriaLabel={t('Create a new mini-game')}
        createDisabledText={
          noPromotion
            ? t('This brand has no promotions yet. Create one in All Promotions & Creatives first.')
            : undefined
        }
        untitledName={t('Untitled mini-game')}
        archiveConfirmTitle={t('Archive this mini-game?')}
        archiveConfirmBody={t(
          'It will be moved to Archived mini-games, where you can restore it later or permanently delete it.',
        )}
        posterOf={(p) => {
          const version = posterVersion(p);
          const url = version?.assets.background_url ?? '';
          // The game at rest over its plate (the plate alone until the game loads, or where previews are unavailable).
          return version && url ? (
            <GamePoster
              plateUrl={url}
              version={version}
              runtime={registry?.runtime ?? null}
              logos={brandLogos}
              title={t('{{name}} preview', { name: p.name || t('Untitled mini-game') })}
            />
          ) : (
            <S.PosterPlaceholder>
              {/* Lumio brand mark for this AI feature (never an approximated sparkle/star). */}
              <LumioMark size={22} />
              <S.PosterEmpty text="Not generated yet" />
            </S.PosterPlaceholder>
          );
        }}
        thumbOf={posterUrl}
        metaOf={(p) => {
          const label = p.ad_params.template_id ? templateLabel(p.ad_params.template_id) : undefined;
          if (!p.ad_params.template_id) return t('No game chosen yet');
          if (!label) return t('Mini-game');
          return t('{{game}} · {{count}} formats', { game: label, count: p.ad_params.formats.length });
        }}
        stateOf={(p) => cardState(p, jobsById)}
        cardFooterSkeleton={<AssetCardStatsSkeleton />}
        cardFooterOf={(p) => (
          <>
            <EmbedStats stats={p.stats} />
            <AssetCardStats
              loaded={stats.loaded}
              failed={stats.failed}
              stats={stats.byId[p.id]}
              hint={newestLockedVersion(p.ad_versions) ? 'get-link' : 'generate-first'}
              onOpen={() => statsSheet.open(p.id)}
            />
          </>
        )}
        onCreate={startNew}
        onOpen={openGame}
        remove={remove}
        restore={restore}
        purge={purge}
        jobsById={jobsById}
      />

      {composerNode}

      {statsFor ? (
        <AssetStatsSheet
          kind="playable"
          creativeId={statsFor.id}
          name={statsFor.name}
          onClose={() => statsSheet.close()}
        />
      ) : null}

      {/* A NEW mini-game while its draft is minted with the welcome bonus (the composer then opens on the game). */}
      {view.kind === 'new' && !created ? (
        <Sheet onClose={goToLibrary} ariaLabel={t('New mini-game')} maxWidth="460px">
          <S.Minting>
            <Skeleton block height={120} radius={12} />
            <S.MintingText text="Setting up your mini-game with your welcome bonus…" />
          </S.Minting>
        </Sheet>
      ) : null}
    </>
  );
};

export default PlayableLibrary;
